<template>
  <div v-if="localCourse" class="term-row">
    <div class="term-col">
      <div class="term">
        <div class="term-item">{{ $t('general.physician') }}:</div>
        <div class="term-value">
          <span v-if="!isEditable">{{ getFullName(localCourse.doctor) }}</span>
          <span v-else class="d-flex">
            <person-search-filter
              @select:item="changeDoctor"
              :find-by="findBy.doctor"
              :find-only-by-this="true"
              class="dropdown-extra"
            >
            </person-search-filter>
            <button
              v-if="isHospitalAdmin && isEditable"
              class="btn icon icon-close icon_primary"
              type="button"
              @click="closeChangingDoctor"
              style="top: -10px"
              :title="$t('src/components/courseinfoblock.1828')"
            ></button>
          </span>
          <svg-icon
            v-if="isHospitalAdmin && !isEditable"
            class="ml-2"
            :name="'edit_pen_inside_the_square'"
            :width="20"
            :height="20"
            @click:icon="openChangingDoctor"
          ></svg-icon>
        </div>
      </div>
      <div class="term">
        <div class="term-item">{{ $t('entity.patient') }}:</div>
        <router-link
          :to="{ path: `/patients/edit/${localCourse.patient.id}` }"
          class="term-value text-secondary"
          >{{ getFullName(localCourse.patient) }}
          <span class="text-nowrap">{{ agePatientStr }}</span></router-link
        >
      </div>
    </div>
    <div class="term-col">
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('src/components/courseinfoblock.2127') }}</div>
        <div class="term-value">{{ courseDuration }} {{ courseDeclensionDuration }}</div>
      </div>
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('src/components/courseinfoblock.523') }}</div>
        <div class="term-value" :class="{ 'term-value-input': isDateEditable }">
          <base-calendar-wrap v-if="isDateEditable" :is-small="true">
            <date-input
              :value="localCourse.close_date"
              @input="changeCloseDate"
              :min-date="minCloseDate"
            ></date-input>
          </base-calendar-wrap>
          <span v-else-if="courseFormattedEndDate"> {{ courseFormattedEndDate }}</span>
        </div>
      </div>
    </div>
    <div class="term-col">
      <div class="term">
        <div class="term-item term-item-md">{{ $t('general.scenario') }}:</div>
        <div class="term-value">{{ courseType }}</div>
      </div>
    </div>
    <base-modal
      :is-show="isShowDateModal"
      @close="resetSavingCloseDate"
      @cancel="resetSavingCloseDate"
      @confirm="confirmSaveCloseDate"
    >
      <template #header>{{ $t('src/components/courseinfoblock.9584') }}</template>
      <template #body>{{ $t('src/components/courseinfoblock.2876') }}</template>
    </base-modal>

    <base-modal
      :is-show="isShowDoctorModal"
      @close="resetSavingDoctor"
      @cancel="resetSavingDoctor"
      @confirm="confirmSaveDoctor"
    >
      <template #header>{{ $t('src/components/courseinfoblock.5061') }}</template>
      <template #body>{{ $t('src/components/courseinfoblock.385') }}</template>
    </base-modal>
  </div>
</template>

<script>
import { fullName } from '@/mixins/full-name.js'
import { courseTypes, DATE_FORMAT, status } from '@/main.config.js'
import { courseTypeList } from '@/modules/course-type-list.js'
import { userRights } from '@/mixins/user-rights'
import declension from '@/modules/declension'
import { courseDate } from '@/mixins/course-date'
import { patientInfo } from '@/mixins/patient-info'
import { FIND_BY_PERSON } from '@/main.config'
import('@/styles/components/terms.less')

export default {
  name: 'CourseInfoForm',
  mixins: [fullName, userRights, courseDate, patientInfo],
  components: {
    BaseCalendarWrap: () => import('@/base/BaseCalendarWrap'),
    DateInput: () => import('@/plugins/DateInput.vue'),
    BaseModal: () => import('@/base/BaseModal.vue'),
    PersonSearchFilter: () => import('@/components/Filters/PersonSearchFilter.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue')
  },
  model: {
    prop: 'course'
  },
  props: {
    course: {
      type: Object,
      required: true
    },
    trainingsInfo: {
      type: Object,
      required: false
    }
  },
  data: () => ({
    localCourse: null,
    isShowDateModal: false,
    isEditable: false,
    isShowDoctorModal: false,
    selectDoctor: null
  }),
  mounted() {
    this.localCourse = Object.assign({}, this.course)
  },
  watch: {
    'course.close_date': {
      handler() {
        this.localCourse.close_date = this.course.close_date
      }
    },
    'course.doctor': {
      handler() {
        this.localCourse = Object.assign({}, this.course)
      }
    }
  },
  computed: {
    courseType() {
      if (!this.course.course_type) {
        return courseTypeList[courseTypes.rehab].title
      }
      return courseTypeList[this.course.course_type].title
    },
    isCourseRehab() {
      return this.course.course_type === courseTypes.rehab || this.course.course_type === null
    },
    isClosedCourse() {
      return this.course.status === status.closed || this.course.status === status.closedWrong
    },
    isDateEditable() {
      return this.course.status && !this.isClosedCourse && this.isManageCourse
    },
    courseDuration() {
      return this.courseMomentEndDate && this.courseMomentStartDate
        ? this.courseMomentEndDate.diff(this.courseMomentStartDate, 'days')
        : null
    },
    courseDeclensionDuration() {
      return this.courseDuration
        ? declension(this.courseDuration, [
            `${this.$tc('unit.day', 0)}`,
            `${this.$tc('unit.day', 1)}`,
            `${this.$tc('unit.day', 2)}`
          ])
        : ''
    },
    minCloseDate() {
      return this.courseMomentStartDate ? this.courseMomentStartDate.format(DATE_FORMAT.server) : null
    },

    findBy() {
      return FIND_BY_PERSON
    },
    agePatientStr() {
      let age = this.getAgeAtSpecificDate(this.localCourse.start_date, this.localCourse.patient.birth_date)
      return age
        ? this.$i18n.locale === 'ru'
          ? `(${age} ${declension(age, [`год`, `года`, `лет`])})`
          : `(${age} years)`
        : ''
    }
  },
  methods: {
    changeCloseDate(date) {
      this.localCourse.close_date = date

      if (!this.trainingsInfo) {
        this.saveCloseDate()
        return
      }
      const trainingLastDate = this.moment(this.trainingsInfo.lastDate)
      if (this.isCourseRehab && trainingLastDate.isAfter(this.moment(date))) {
        this.showDateModal()
        return
      }

      this.saveCloseDate()
    },
    saveCloseDate() {
      this.$emit('save:course', {
        close_date: this.localCourse.close_date
      })
    },
    confirmSaveCloseDate() {
      this.saveCloseDate()
      this.closeDateModal()
    },
    showDateModal() {
      this.isShowDateModal = true
    },
    closeDateModal() {
      this.isShowDateModal = false
    },
    resetSavingCloseDate() {
      this.closeDateModal()
      this.localCourse.close_date = this.course.close_date
    },
    openChangingDoctor() {
      this.isEditable = true
    },
    closeChangingDoctor() {
      this.isEditable = false
    },
    changeDoctor(data) {
      this.isShowDoctorModal = true
      this.selectDoctor = data.id
    },
    resetSavingDoctor() {
      this.closeDoctorModal()
      this.closeChangingDoctor()
    },
    closeDoctorModal() {
      this.isShowDoctorModal = false
    },
    confirmSaveDoctor() {
      this.saveDoctor()
    },
    saveDoctor() {
      this.$emit('save:course', {
        doctor_id: this.selectDoctor
      })
      this.closeDoctorModal()
      this.isEditable = false
    }
  }
}
</script>
